import { useMemo, useState } from 'react'
import { useToast } from '@winnin/winnin_ui.components.toast'

import { useTranslate } from 'lib/tolgee'
import { TOLGEE_GLOBALS } from 'utils'

import type { IUseNicheSelection, TNicheSelectionItem, TOnNicheToggle } from './use-niche-selection.types'
import { NICHE_LIMIT, NICHE_MIN_LIMIT, NICHE_PREFIX } from '../../niche.consts'
import { resolveNicheSelectionTerritoryId } from '../../promoted-niche.utils'

/**
 * Shared niche-selection state for the "create dashboard" modals (Niche and
 * Category Map): which niches are checked, the resolved territory ids to send
 * to the mutation, and the shared min/limit rules. Per-toggle analytics stay
 * with the caller via `onToggleNiche` so tracking payloads remain model-owned.
 */
export const useNicheSelection = <T extends TNicheSelectionItem>(
  nicheList: T[],
  onToggleNiche?: TOnNicheToggle<T>
): IUseNicheSelection => {
  const [selectedNiches, setSelectedNiches] = useState<string[]>([])

  const { createToast } = useToast()
  const t = useTranslate()

  const firstNiches = useMemo(() => {
    return nicheList.map(item => item.id).slice(0, NICHE_LIMIT)
  }, [nicheList])

  const effectiveNiches = useMemo(() => {
    return selectedNiches.length ? selectedNiches : firstNiches
  }, [firstNiches, selectedNiches])

  // Map each item's bare id to the canonical territory it represents, so a
  // promoted Category in the list is sent as its `industry_topic.{uuid}` rather
  // than blindly prefixed as `niche.{uuid}`.
  const territoryIdByNicheId = useMemo(() => {
    const map = new Map<string, string>()
    nicheList.forEach(item => map.set(item.id, resolveNicheSelectionTerritoryId(item)))
    return map
  }, [nicheList])

  const nicheTerritoryIds = useMemo(() => {
    return effectiveNiches.map(
      item => territoryIdByNicheId.get(item) ?? (item.startsWith(NICHE_PREFIX) ? item : `${NICHE_PREFIX}${item}`)
    )
  }, [effectiveNiches, territoryIdByNicheId])

  const handleSelectNiche = (selectedNiche: string) => {
    const notifyToggle = (action: 'Select' | 'Deselect') => {
      const niche = nicheList.find(item => item.id === selectedNiche)
      if (niche) onToggleNiche?.(niche, action)
    }

    if (!selectedNiches.length && nicheList?.length >= NICHE_LIMIT) {
      if (firstNiches.includes(selectedNiche)) {
        setSelectedNiches(firstNiches.filter(item => item !== selectedNiche))
        notifyToggle('Deselect')
        return
      }
      createToast({
        content: t(TOLGEE_GLOBALS.nicheLimitMessage(NICHE_LIMIT)),
      })
      return
    }
    if (selectedNiches.includes(selectedNiche)) {
      setSelectedNiches(prev => prev.filter(item => item !== selectedNiche))
      notifyToggle('Deselect')
      return
    }
    if (selectedNiches.length >= NICHE_LIMIT) {
      createToast({
        content: t(TOLGEE_GLOBALS.nicheLimitMessage(NICHE_LIMIT)),
      })
      return
    }
    setSelectedNiches(prev => [...prev, selectedNiche])
    notifyToggle('Select')
  }

  const nicheListWithActive = useMemo(() => {
    return nicheList.map(item => {
      const isInFirstNiches = firstNiches.includes(item.id)
      const isInSelectedNiches = selectedNiches.includes(item.id)
      return {
        name: item.name,
        id: item.id,
        isActive: selectedNiches.length ? isInSelectedNiches : isInFirstNiches,
      }
    })
  }, [firstNiches, nicheList, selectedNiches])

  const isBelowNicheMinLimit = effectiveNiches.length < NICHE_MIN_LIMIT

  return {
    selectedNiches,
    effectiveNiches,
    nicheListWithActive,
    nicheTerritoryIds,
    isBelowNicheMinLimit,
    handleSelectNiche,
  }
}
